import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { Observable, Subject } from "rxjs";
import { HttpClient, HttpEvent, HttpParams, HttpHeaders } from "@angular/common/http";
import { Urls } from "../shared/urls";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from "./message.service";
import { NotifyService } from "./notify.service";
import { saveAs } from "file-saver";
import { GetResponse } from "../interfaces/common-response.interface";
import {
  AuditLogListInterface,
  KycLogData,
  MonitoringAssigneeOption,
  MonitoringBadges,
  MonitoringCustomerOption,
  MonitoringListRequest,
  MonitoringWarningChecks
} from "../interfaces/audit-log.interface";
import { AdverseMediaCustomerDetails, AdverseMediaList } from "../interfaces/adverse-media.interface";
import {
  CustomerPoliticalInvolvementDetails,
  CustomerSanctionsDetails,
  PoliticalInvolvementsList,
  SanctionsListResponse
} from "../interfaces/sanctions-and-political.interface";
import { customerTypeEnum } from "../enums/customerType.enum";
import { categoryEnum } from "../enums/category.enum";
import { buildVerificationReportFileName, DownloadNamingCustomer } from "../utils/download-file-name.util";
import { VerificationReportStatus } from "../interfaces/customer-background-check.interface";
import {
  AddExistingMemberBatchRequest,
  AddExistingMemberPayload,
  RawSuggestionGroup,
  RelationApiItem,
  SuggestionCountMap
} from "../interfaces/relation-list.interface";

@Injectable({
  providedIn: "root"
})
export class CustomerService {
  isCopyURLInProgress = false;
  isDownloadDocumentInProgress = false;
  isSendReminderInProgress = false;
  getTaskSubject = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private messageService: MessageService,
    private notificationService: NotifyService,
    private translateService: TranslateService
  ) {}

  async copyDocumentUrl(url: string, progressPercentage) {
    if (!this.isCopyURLInProgress && url && progressPercentage !== 100) {
      this.isCopyURLInProgress = true;
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      await this.notificationService.success("Copied");
      this.isCopyURLInProgress = false;
    }
  }

  /**
   * @param naming Present only for finished verification reports, which are named after the customer they were
   * produced for. Anything else — other categories, or a partially filled in task — keeps the generic
   * `document-<timestamp>-<id>` name, so a partial download stays recognisable as such.
   * @param preConfirmed Lets a caller that has already confirmed with its own dialog skip the SweetAlert
   * here. The Tasks tab confirms with a `ui-modal` so it passes true; every legacy caller omits it.
   */
  async customerDetailDownloadDocument(
    id,
    progressPercentage,
    isComplete,
    naming?: { customer?: DownloadNamingCustomer; category?: string; createdAt?: string | Date },
    preConfirmed = false
  ) {
    const data = {
      timestamp: Date.now()
    };
    let result,
      documentName = "document-";
    if (!this.isDownloadDocumentInProgress && progressPercentage !== 0) {
      try {
        this.isDownloadDocumentInProgress = true;
        if (progressPercentage !== 100) {
          result = preConfirmed ? { value: true } : await this.messageService.confirmDownload();
          documentName = "partial-document-";
        }
        if (progressPercentage === 100 || result.value) {
          const res: any = await this.downloadDocument(id, "");
          const type = isComplete ? ".pdf" : ".doc";
          const reportFileName =
            progressPercentage === 100 && naming?.category === categoryEnum.verification
              ? buildVerificationReportFileName(naming.customer, naming.createdAt, type)
              : null;
          await saveAs(res, reportFileName ?? `${documentName}${data.timestamp}-${id}${type}`);
        }
        this.isDownloadDocumentInProgress = false;
        return "success";
      } catch (err) {
        this.isDownloadDocumentInProgress = false;
        return err;
      }
    }
  }

  async customerDetailSendReminder(data) {
    if (!this.isSendReminderInProgress) {
      try {
        this.isSendReminderInProgress = true;
        await this.apiService.post("customer/reminder", data);
        this.isSendReminderInProgress = false;
        return "success";
      } catch (err) {
        this.isSendReminderInProgress = false;
        return err;
      }
    }
  }

  async customerDetailDeleteDocument(id) {
    const result: any = await this.messageService.simpleDelete();
    if (result.value) {
      try {
        await this.deleteDocument(id);
        this.notificationService.success("Document Deleted Successfully");
        return "success";
      } catch (err) {
        return err;
      }
    }
  }

  async getResponsible(params) {
    return await this.apiService.get("user/all", params);
  }

  async addCustomer(data) {
    return await this.apiService.post("customer", data);
  }

  async updateDoubleCustomerData(
    updateField: string,
    selectedCustomerArray: Array<object>,
    advanceLabels?: { field: string; labels: string[] }[],
    labels?: string[]
  ) {
    const payload: {
      fieldToUpdate: string;
      customers: Array<object>;
      newAdvanceLabels?: { field: string; labels: string[] }[];
      newLabels?: string[];
    } = {
      fieldToUpdate: updateField,
      customers: selectedCustomerArray
    };
    if (advanceLabels?.length) {
      payload.newAdvanceLabels = advanceLabels;
    }
    if (labels?.length) {
      payload.newLabels = labels;
    }
    return await this.apiService.patch(`customer/overwrite-customer-details/excel`, payload);
  }

  async getCustomersList(params: HttpParams) {
    return await this.apiService.get("customer", params);
  }

  async getAdverseMediaList(params: HttpParams): Promise<GetResponse<AdverseMediaList[]>> {
    return await this.apiService.get("adverse-media", params);
  }

  async getSanctionsList(params: HttpParams): Promise<GetResponse<SanctionsListResponse[]>> {
    return await this.apiService.get("sanctions-and-political-involvement/sanctions", params);
  }

  async getPoliticalInvolvementsList(params: HttpParams): Promise<GetResponse<PoliticalInvolvementsList[]>> {
    return await this.apiService.get("sanctions-and-political-involvement/political-involvements", params);
  }

  async getCustomerSanctionsAndPoliticalInvolvement(customerId: string): Promise<GetResponse<CustomerSanctionsDetails>> {
    return await this.apiService.get(`sanctions-and-political-involvement/customer/${customerId}`);
  }

  async getCustomerSpecificAdverseMedia(customerId: string): Promise<GetResponse<AdverseMediaCustomerDetails>> {
    return await this.apiService.get(`adverse-media/customer/${customerId}`);
  }

  async getCustomerPepDetails(customerId: string): Promise<GetResponse<CustomerPoliticalInvolvementDetails>> {
    return await this.apiService.get(`sanctions-and-political-involvement/customer/${customerId}/pep`);
  }

  async getTypeBasedCustomers(params: HttpParams) {
    return await this.apiService.get("customer/type-based-customers", params);
  }

  async getCustomerById(id, params?) {
    return await this.apiService.get(`customer/${id}`, params);
  }

  async updateCustomer(data, id) {
    return await this.apiService.put(`customer/${id}`, data);
  }

  async uploadPrivatePersonIDCard(data) {
    return await this.apiService.post(`customer/upload/id-card`, data);
  }

  async downloadPrivatePersonIDCard(id) {
    return await this.apiService.get(`additional-upload/${id}/download`);
  }

  async deleteCustomer(id) {
    return await this.apiService.delete(`customer/${id}`);
  }

  async deleteMultipleCustomers(data) {
    return await this.apiService.put("customer/delete", data);
  }

  async getAllDocuments(id) {
    return await this.apiService.get(`transcript/customer/${id}`);
  }

  async downloadDocument(id, data) {
    return new Promise((resolve, reject) => {
      this.http
        .post(Urls.download_contract + id, data, {
          responseType: "blob"
        })
        .subscribe(
          res => {
            resolve(res);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  // Preview always requests a PDF, generated on demand for in-progress tasks — separate endpoint
  // from downloadDocument, which returns the task's native format (DOCX until finalized).
  async previewDocument(id): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http
        .post(Urls.transcript + id + "/preview", null, {
          responseType: "blob"
        })
        .subscribe(
          res => {
            resolve(res);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  async deleteDocument(id) {
    return await this.apiService.delete(`transcript/${id}`);
  }

  async assignDocumentsToCustomers(data) {
    return await this.apiService.post("transcript/assignDocument", data);
  }

  async getVerificationReportGenerationStatus(
    customerId: string
  ): Promise<{ isGenerating: boolean; failed?: boolean; documentId?: string }> {
    const res = await this.apiService.get(`transcript/customer/${customerId}/verification-report-status`);
    return res?.data || { isGenerating: false };
  }

  async getVerificationReportGenerationStatuses(customerIds: string[]): Promise<VerificationReportStatus[]> {
    const res = await this.apiService.post("transcript/verification-report-status", { customerIds });
    return res?.data?.statuses || [];
  }

  async getActiveVerificationReportGenerationCustomerIds(): Promise<string[]> {
    const res = await this.apiService.get("transcript/verification-report-status/active");
    return res?.data?.customerIds || [];
  }

  async getDocumentById(id) {
    return await this.apiService.get(`transcript/riskOverview/${id}`);
  }

  async updateDocumentAssignment(id, data) {
    return await this.apiService.post(`transcript/reassign-internal-task/${id}`, { internalUserId: data });
  }

  /** `savedSuggestions` skips re-checking the OTB and UBO register, which is slow, for the last check's suggestions. */
  async getAllMembers(
    customerId: string,
    savedSuggestions = false
  ): Promise<
    GetResponse<{
      relation: RelationApiItem[];
      suggestion: RawSuggestionGroup[];
      suggestionCount?: SuggestionCountMap;
    }>
  > {
    const params = savedSuggestions ? new HttpParams().set("savedSuggestions", "true") : new HttpParams();
    return await this.apiService.get(`customer/${customerId}/relations`, params);
  }

  async getRelationDataByCustomerId(customerId: string, relationId: string, relationType: customerTypeEnum): Promise<any> {
    let params = new HttpParams();
    params = params.append("relationType", String(relationType));
    return this.apiService.get(`customer/${customerId}/relation/${relationId}/roles`, params);
  }

  async getMemberById(id: string, skipLogoutOn401: boolean = false) {
    let headers;
    if (skipLogoutOn401) {
      headers = new HttpHeaders({ "X-Skip-Logout-On-401": "true" });
    }
    return await this.apiService.get(`customer/${id}`, new HttpParams(), headers);
  }

  /**
   * Adds existing members as relations in batch format.
   * @param data - Single relation payload or array of relation payloads
   * @returns Promise with batch operation results
   */
  async addExistingMember(data: AddExistingMemberPayload | AddExistingMemberPayload[]): Promise<AddExistingMemberBatchRequest> {
    // Normalize to array format for batch API
    const relations: AddExistingMemberPayload[] = Array.isArray(data) ? data : [data];
    return await this.apiService.post(`customer/relation/add-existing`, { relations });
  }

  async keepRole(customerId: string, relationId: string, reference: string) {
    return await this.apiService.patch(`customer/${customerId}/relation/${relationId}/keep-role`, { reference });
  }

  async deleteMember(id: string) {
    return await this.apiService.delete(`customer/${id}`);
  }

  async uploadDocuments(id, data) {
    return await this.apiService.post(`additional-upload/upload/${id}`, data);
  }

  async uploadSnapshotDocuments(id, data) {
    return await this.apiService.post(`additional-upload/upload/snapshot/${id}`, data);
  }

  async getAllUploads(id) {
    return await this.apiService.get(`additional-upload/${id}`);
  }

  async getUploadDetailsById(companyId, uploadId) {
    return await this.apiService.get(`additional-upload/${companyId}/${uploadId}`);
  }

  async updateUploadedDetails(companyId, uploadId, data) {
    return await this.apiService.put(`additional-upload/${companyId}/${uploadId}`, data);
  }

  async updateUploadedFileName(uploadId: string, updatedFileName: string): Promise<unknown> {
    return await this.apiService.patch(`additional-upload/${uploadId}`, { updatedFileName });
  }

  async downloadUploadedDocument(data: { customerId: string; fileNames: string[]; type?: string }): Promise<unknown> {
    return await this.apiService.post("additional-upload/download", data);
  }

  async deleteUploadedDocument(companyId: string, uploadId: string) {
    return await this.apiService.delete(`additional-upload/${companyId}/${uploadId}`);
  }

  excelUpload(data): Observable<HttpEvent<any>> {
    return this.http.post(Urls.baseUrl + "customer/import-customer-excel", data, {
      reportProgress: true,
      observe: "events"
    });
  }

  async bulkImportCustomers(data) {
    return this.apiService.post("customer/import", data);
  }

  async sendMail(transcriptId) {
    return await this.apiService.post(`transcript/sendMail/${transcriptId}`, {});
  }

  async getKBODetails(kboNumber, language: string = "dutch") {
    return await this.apiService.get(`kbo/${kboNumber}`, new HttpParams().append("language", language));
  }

  async validateRRNOrKBO(data: { fieldType: "RRN" | "KBO"; value: string; country: string }) {
    return await this.apiService.post(`customer/validate/rrn-kbo-number`, data);
  }

  archiveCustomer(customerId: string) {
    return this.http.patch(`${Urls.customer}${customerId}/archive`, null);
  }

  unArchiveCustomer(customerId: string) {
    return this.http.patch(`${Urls.customer}${customerId}/un-archive`, null);
  }

  unArchiveMultipleCustomers(data) {
    return this.apiService.patch("customer/un-archive/bulk", data);
  }

  unlinkRelation(customerId: string, relationId: string, data) {
    return this.apiService.patch(`customer/${customerId}/relation/${relationId}/unlink`, data);
  }

  async mergeCustomers(data: { customerIds: string[]; type: string }) {
    await this.apiService.post("customer/merge-customers", data);
  }

  /** The Monitoring page: every customer the user can access, archived customers left out. */
  async getMonitoringList(body: MonitoringListRequest): Promise<GetResponse<AuditLogListInterface<KycLogData>>> {
    return await this.apiService.post(Urls.monitoringListPath, body);
  }

  async getMonitoringAssigneeOptions(customerId: string, limit?: number): Promise<GetResponse<MonitoringAssigneeOption[]>> {
    let params = new HttpParams();
    if (limit) {
      params = params.set("limit", limit);
    }
    return await this.apiService.get(Urls.monitoringAssigneeOptionsPath(customerId), params);
  }

  async getMonitoringCustomerOptions(search: string, limit: number): Promise<GetResponse<MonitoringCustomerOption[]>> {
    const params = new HttpParams().set("search", search).set("limit", limit);
    return await this.apiService.get(Urls.monitoringCustomerFilterOptionsPath, params);
  }

  /** Saves a monitoring event's assignee; null sets a reviewed event back to "Complete". Returns the updated row. */
  async updateMonitoringAssignee(eventId: string, assignee: string | null): Promise<GetResponse<KycLogData>> {
    return await this.apiService.patch(Urls.monitoringEventAssigneePath(eventId), { assignee });
  }

  async getMonitoringBadges(customerId: string): Promise<GetResponse<MonitoringBadges>> {
    return await this.apiService.get(Urls.monitoringCustomerBadgesPath(customerId));
  }

  async getWarningChecks(customerId: string): Promise<GetResponse<MonitoringWarningChecks>> {
    return await this.apiService.get(Urls.monitoringCustomerWarningChecksPath(customerId));
  }

  async getCustomerMonitoringList(
    customerId: string,
    body: MonitoringListRequest
  ): Promise<GetResponse<AuditLogListInterface<KycLogData>>> {
    return await this.apiService.post(Urls.monitoringCustomerListPath(customerId), body);
  }

  async getAuditLogList(customerId: string, params?) {
    return await this.apiService.post(`audit-log/${customerId}/logs`, params);
  }
  saveMinfinConfiguration(customerId: string, data: any) {
    return this.apiService.patch(`minfin/customer/${customerId}/configuration`, data);
  }
}
